<template>
  <!-- Loading placeholder -->
  <span
    v-if="!src || error"
    :class="[
      'inline-flex items-center justify-center flex-shrink-0 transition-all duration-200 bg-gray-200 fade-in overflow-hidden',
      roundedClass,
      sizeClasses,
      { 'scale-125': highlighted },
      ring ? 'ring-1 ring-white ring-offset-2' : ''
    ]"
    :style="
      highlighted
        ? {
            width: sizePx,
            height: sizePx,
            minWidth: sizePx,
            minHeight: sizePx,
            maxWidth: sizePx,
            maxHeight: sizePx,
            '--tw-ring-color': '#84cc16',
            '--tw-ring-width': '4px'
          }
        : {
            width: sizePx,
            height: sizePx,
            minWidth: sizePx,
            minHeight: sizePx,
            maxWidth: sizePx,
            maxHeight: sizePx
          }
    "
  >
    <User :class="['text-gray-500', size === '2xl' ? 'h-8 w-8' : 'h-5 w-5']" />
  </span>

  <!-- Actual image with fade-in effect -->
  <img
    v-if="src && !error"
    :src="src"
    :alt="alt"
    :class="[
      'flex-shrink-0 object-cover transition-all duration-300 overflow-hidden',
      roundedClass,
      sizeClasses,
      { 'opacity-0': !imageLoaded, 'opacity-100': imageLoaded, 'scale-125': highlighted },
      ring ? 'ring-1 ring-white ring-offset-2' : ''
    ]"
    :style="
      highlighted
        ? {
            width: sizePx,
            height: sizePx,
            minWidth: sizePx,
            minHeight: sizePx,
            maxWidth: sizePx,
            maxHeight: sizePx,
            '--tw-ring-color': '#84cc16',
            boxShadow: '0 0 0 3px #84cc16'
          }
        : {
            width: sizePx,
            height: sizePx,
            minWidth: sizePx,
            minHeight: sizePx,
            maxWidth: sizePx,
            maxHeight: sizePx
          }
    "
    @load="onImageLoad"
    @error="onImageError"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { User } from 'lucide-vue-next'

interface Props {
  src?: string | null
  alt?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  highlighted?: boolean
  /** Show white ring (useful for overlapping avatars) */
  ring?: boolean
  /** Corner rounding: 'full' for a circle, 'lg' for a rounded square */
  rounded?: 'full' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  alt: 'Avatar',
  size: 'md',
  highlighted: false,
  ring: false,
  rounded: 'full'
})

const imageLoaded = ref(false)
const error = ref(false)

const roundedClass = computed(() => (props.rounded === 'lg' ? 'rounded-lg' : 'rounded-full'))

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-6 w-6'
    case 'md':
      return 'h-8 w-8'
    case 'lg':
      return 'h-10 w-10'
    case 'xl':
      return 'h-12 w-12'
    case '2xl':
      return 'h-16 w-16'
    default:
      return 'h-8 w-8'
  }
})

const sizePx = computed(() => {
  switch (props.size) {
    case 'sm':
      return '24px'
    case 'md':
      return '32px'
    case 'lg':
      return '40px'
    case 'xl':
      return '48px'
    case '2xl':
      return '64px'
    default:
      return '32px'
  }
})

const onImageLoad = () => {
  imageLoaded.value = true
  error.value = false
}

const onImageError = () => {
  error.value = true
  imageLoaded.value = false
}
</script>
